import type {
  LEARNING_RESOURCE_REFERENCE_TYPES,
  LEARNING_RESOURCE_REFERENCES_SCOPE,
  LEARNING_RESOURCE_TYPE,
  LEARNING_RESOURCE_VIEW,
} from '@didaskill/domain/learning-resources/constants';
import type {
  CreateLearningResourceBody,
  GetLearningResourcesPresentersQuery,
  LearningResource,
  LearningResourcesTags,
  UpdateLearningResourceBody,
} from '@didaskill/domain/learning-resources/types';
import type { LearningResourceEngagement } from '@didaskill/domain/notations/types';
import type { EmployeeViewMinimal } from '@didaskill/domain/users/types';

import { HttpRepository } from '../httpRepository';

const e = encodeURIComponent;

const LEARNING_RESOURCE_UPLOAD_API_URL = '/api/v1/file/downloads/learning-resources';
const LEARNING_RESOURCE_API_URL = '/api/v1/school/learning-resources';

export class LearningResourcesRepository extends HttpRepository {
  /**
   * Get learning resources.
   * @param courseId - Course id.
   * @param classId - Class id.
   * @param courseIds - Course ids.
   * @param lessonIds - Lesson ids.
   * @param virtualClassroomIds - Virtual classroom ids.
   * @param presenterIds - Presenter (employee) ids.
   * @param type - Learning resource type.
   * @param tags - Learning resource tags.
   * @param href - Search for learning resources with a href containing the given string.
   * @param offset - Pagination offset.
   * @param limit - Pagination limit.
   * @param sort - Sort option.
   * @param q - Search text.
   * @param view - Learning resource view (student or FULL).
   * @param referencesScope - Scope for including unscoped references when the view is STUDENT (default is STRICT).
   * @returns List of learning resources, including lesson resources and virtual classrooms replays.
   */
  async getLearningResources<T extends LearningResource>({
    courseId,
    classId,
    courseIds,
    lessonIds,
    virtualClassroomIds,
    presenterIds,
    types,
    tags,
    href,
    offset,
    limit,
    sort,
    q,
    view,
    referencesScope,
    skipOutdated,
  }: {
    courseId?: string;
    classId?: string;
    courseIds?: string[];
    lessonIds?: string[];
    virtualClassroomIds?: string[];
    presenterIds?: string[];
    types?: LEARNING_RESOURCE_TYPE[];
    tags?: LearningResourcesTags[];
    href?: string;
    offset?: number;
    limit?: number;
    q?: string;
    view?: LEARNING_RESOURCE_VIEW;
    sort?: string;
    referencesScope?: LEARNING_RESOURCE_REFERENCES_SCOPE;
    skipOutdated?: boolean;
  }): Promise<{ count: number; learningResources: T[] }> {
    const { body } = await this.http.get(LEARNING_RESOURCE_API_URL).query({
      courseId,
      classId,
      'courseIds[]': courseIds ? courseIds : [],
      'lessonIds[]': lessonIds ? lessonIds : [],
      'virtualClassroomIds[]': virtualClassroomIds ? virtualClassroomIds : [],
      'presenterIds[]': presenterIds ? presenterIds : [],
      'types[]': types ? types : [],
      'tags[]': tags ? tags : [],
      href,
      view,
      sort,
      offset,
      limit,
      q,
      referencesScope,
      skipOutdated,
    });

    return body;
  }

  /**
   * Get distinct presenters linked to learning resources in the given access scope.
   * @param query Course/class and scope filters.
   * @returns Presenters that appear on at least one matching resource.
   */
  async getLearningResourcesPresenters(
    query: GetLearningResourcesPresentersQuery = {},
  ): Promise<{ presenters: EmployeeViewMinimal[] }> {
    const { body } = await this.http.get(`${LEARNING_RESOURCE_API_URL}/presenters`).query({
      courseId: query.courseId,
      classId: query.classId,
      'courseIds[]': query.courseIds ? query.courseIds : [],
      'lessonIds[]': query.lessonIds ? query.lessonIds : [],
      'virtualClassroomIds[]': query.virtualClassroomIds ? query.virtualClassroomIds : [],
      view: query.view,
      referencesScope: query.referencesScope,
      skipOutdated: query.skipOutdated,
    });

    return body;
  }

  /**
   * Fetch learning resource.
   *
   * @param learningResourceId - Learning resource id.
   * @returns - Learning resource.
   */
  async getLearningResource(learningResourceId: string): Promise<LearningResource> {
    const { body: learningResource } = await this.http.get(
      `/api/v1/school/learning-resources/${e(learningResourceId)}`,
    );

    return learningResource;
  }

  /**
   * Fetches likes, dislikes, reports and the feedback list of a learning resource.
   * @param learningResourceId Learning resource id.
   * @returns Feedback counts and the list of ratings and reports.
   */
  async getLearningResourceEngagement(
    learningResourceId: string,
  ): Promise<LearningResourceEngagement> {
    const { body } = await this.http.get(
      `${LEARNING_RESOURCE_API_URL}/${e(learningResourceId)}/feedbacks`,
    );

    return body as LearningResourceEngagement;
  }

  /**
   * Download a file learning resource.
   *
   * @param learningResourceId - Learning resource id.
   * @param uploadId - Upload id.
   * @returns - File.
   */
  async downloadLearningResource(
    learningResourceId: string,
    uploadId: string,
  ): Promise<{ type: string; body: Blob }> {
    const { body, type } = await this.http
      .get(`${LEARNING_RESOURCE_UPLOAD_API_URL}/${e(learningResourceId)}/upload/${e(uploadId)}`)
      .responseType('blob');

    return { body, type };
  }

  /**
   * Create a learning resource.
   * @param body - Learning resource data.
   * @returns - Created learning resource.
   */
  async createLearningResource(body: CreateLearningResourceBody): Promise<LearningResource> {
    const { body: createdResource } = await this.http.post(LEARNING_RESOURCE_API_URL).send(body);

    return createdResource;
  }

  /**
   * Update a learning resource.
   * @param learningResourceId - Learning resource id.
   * @param body - Learning resource data to update.
   * @returns - Updated learning resource.
   */
  async updateLearningResource(
    learningResourceId: string,
    body: UpdateLearningResourceBody,
  ): Promise<LearningResource> {
    const { body: updatedResource } = await this.http
      .patch(`${LEARNING_RESOURCE_API_URL}/${e(learningResourceId)}`)
      .send(body);

    return updatedResource;
  }

  /**
   * Delete a learning resource.
   * @param learningResourceId - Learning resource id.
   * @returns - Void.
   */
  async deleteLearningResource(learningResourceId: string): Promise<void> {
    await this.http.delete(`${LEARNING_RESOURCE_API_URL}/${e(learningResourceId)}`);
  }

  /**
   * Increment the views count of a learning resource.
   * @param learningResourceId - Learning resource id.
   * @returns - Void.
   */
  async incrementLearningResourceViewsCount(learningResourceId: string): Promise<void> {
    await this.http.post(`${LEARNING_RESOURCE_API_URL}/${e(learningResourceId)}/views`);
  }

  /**
   * Add a reference to a learning resource.
   * @param learningResourceId - Learning resource id.
   * @param referenceType - Reference type (course, lesson, virtual classroom).
   * @param referenceId - Reference id.
   * @returns - Void.
   */
  async addLearningResourceReference(
    learningResourceId: string,
    referenceType: LEARNING_RESOURCE_REFERENCE_TYPES,
    referenceId: string,
  ): Promise<LearningResource> {
    const { body: updatedResource } = await this.http
      .post(`${LEARNING_RESOURCE_API_URL}/${e(learningResourceId)}/references`)
      .send({
        type: referenceType,
        referenceId,
      });

    return updatedResource;
  }

  /**
   * Delete a reference from a learning resource.
   * @param learningResourceId - Learning resource id.
   * @param referenceType - Reference type (course, lesson, virtual classroom).
   * @param referenceId - Reference id.
   * @returns - Void.
   */
  async deleteLearningResourceReference(
    learningResourceId: string,
    referenceType: LEARNING_RESOURCE_REFERENCE_TYPES,
    referenceId: string,
  ): Promise<LearningResource> {
    const { body: updatedResource } = await this.http
      .delete(`${LEARNING_RESOURCE_API_URL}/${e(learningResourceId)}/references`)
      .send({
        type: referenceType,
        referenceId,
      });

    return updatedResource;
  }
}
